import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useUserStore } from '../store/userStore';
import { useTheme } from '../contexts/ThemeContext';
import { playNavigationSound, playLogoutSound, isSoundEnabled, setSoundEnabled } from '../hooks/useSound';

interface LayoutProps {
  children: React.ReactNode;
}

// ── Constantes de ancho (sidebar desktop) ─────────────────────────────────────
const SIDEBAR_MIN         = 64;
const SIDEBAR_MAX         = 300;
const SIDEBAR_DEFAULT     = 208;
const COLLAPSED_THRESHOLD = 90;

// ── Iconos SVG ────────────────────────────────────────────────────────────────
const IconDashboard = ({ active }: { active: boolean }) => (
  <svg className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-yellow' : 'text-brand-mid'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 2.2 : 1.8}
      d="M3 3h7v7H3V3zm11 0h7v7h-7V3zM3 14h7v7H3v-7zm11 0h7v7h-7v-7z" />
  </svg>
);
const IconConversations = ({ active }: { active: boolean }) => (
  <svg className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-yellow' : 'text-brand-mid'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 2.2 : 1.8}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);
const IconAgents = ({ active }: { active: boolean }) => (
  <svg className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-yellow' : 'text-brand-mid'}`}
    viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={active ? 2.2 : 1.8} strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="2" x2="12" y2="5" />
    <circle cx="12" cy="2" r="1" fill="currentColor" stroke="none" />
    <rect x="4" y="5" width="16" height="12" rx="2" />
    <circle cx="9" cy="11" r="1.5" fill="currentColor" stroke="none" />
    <circle cx="15" cy="11" r="1.5" fill="currentColor" stroke="none" />
    <path d="M9 14.5h6" strokeWidth="1.5" />
    <line x1="4" y1="10" x2="2" y2="10" />
    <line x1="20" y1="10" x2="22" y2="10" />
  </svg>
);
const IconUsers = ({ active }: { active: boolean }) => (
  <svg className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-yellow' : 'text-brand-mid'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 2.2 : 1.8}
      d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zm8 4v6m3-3h-6" />
  </svg>
);
const IconBoard = ({ active }: { active: boolean }) => (
  <svg className={`w-5 h-5 flex-shrink-0 ${active ? 'text-brand-yellow' : 'text-brand-mid'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={active ? 2.2 : 1.8}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
  </svg>
);

// ── NavItem (sidebar desktop) ─────────────────────────────────────────────────
interface NavItemProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
}
const NavItem: React.FC<NavItemProps> = ({ label, icon, active, collapsed, onClick }) => (
  <button
    onClick={onClick}
    title={collapsed ? label : undefined}
    className={`
      w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 text-sm font-medium overflow-hidden
      ${active
        ? 'nav-item-active bg-brand-dark text-white border-l-[3px] border-brand-yellow pl-[calc(0.75rem-3px)]'
        : 'text-brand-mid hover:bg-brand-light hover:text-brand-dark border-l-[3px] border-transparent pl-[calc(0.75rem-3px)]'
      }
      ${collapsed ? 'justify-center' : ''}
    `}
  >
    {icon}
    {!collapsed && <span className="truncate whitespace-nowrap">{label}</span>}
  </button>
);

// ── BottomNavItem (mobile) ────────────────────────────────────────────────────
interface BottomNavItemProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  badge?: number;
}
const BottomNavItem: React.FC<BottomNavItemProps> = ({ label, icon, active, onClick, badge }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2 transition-colors relative
      ${active ? 'text-brand-yellow' : 'text-brand-mid'}`}
  >
    <div className="relative">
      {icon}
      {badge && badge > 0 ? (
        <span className="absolute -top-1 -right-1 bg-brand-yellow text-brand-dark text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">
          {badge > 9 ? '9+' : badge}
        </span>
      ) : null}
    </div>
    <span className="text-[10px] font-medium leading-none">{label}</span>
    {active && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-brand-yellow rounded-full" />}
  </button>
);

// ── Layout principal ──────────────────────────────────────────────────────────
const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout }       = useAuthStore();
  const { isAdmin, isAdvisor, user: userProfile } = useUserStore();
  const { isDark, toggleTheme } = useTheme();
  const [soundEnabled, setSoundState] = useState(isSoundEnabled());
  const [menuOpen, setMenuOpen]       = useState(false);
  const menuRef    = useRef<HTMLDivElement>(null);
  const sheetRef   = useRef<HTMLDivElement>(null);
  const avatarBtnRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);

  // ── Sidebar desktop (redimensionable) ────────────────────────────────
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem('sidebar_width');
    if (saved) {
      const n = parseInt(saved, 10);
      if (!isNaN(n) && n >= SIDEBAR_MIN && n <= SIDEBAR_MAX) return n;
    }
    return SIDEBAR_DEFAULT;
  });

  const isDragging     = useRef(false);
  const dragStartX     = useRef(0);
  const dragStartWidth = useRef(0);
  const collapsed = sidebarWidth < COLLAPSED_THRESHOLD;

  const handleToggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    setSoundState(next);
  };

  const handleNav = (path: string) => {
    playNavigationSound();
    navigate(path);
  };

  const handleLogout = () => { playLogoutSound(); logout(); navigate('/login'); };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const inMenu   = menuRef.current?.contains(e.target as Node);
      const inSheet  = sheetRef.current?.contains(e.target as Node);
      const inAvatar = avatarBtnRef.current?.contains(e.target as Node);
      if (!inMenu && !inSheet && !inAvatar) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current) return;
    const delta = e.clientX - dragStartX.current;
    const next  = Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, dragStartWidth.current + delta));
    setSidebarWidth(next);
  }, []);

  const onMouseUp = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    document.body.style.cursor     = '';
    document.body.style.userSelect = '';
    setSidebarWidth(prev => { localStorage.setItem('sidebar_width', String(prev)); return prev; });
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove]);

  const onDragHandleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current     = true;
    dragStartX.current     = e.clientX;
    dragStartWidth.current = sidebarWidth;
    document.body.style.cursor     = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  const onDragHandleDblClick = () => {
    const next = collapsed ? SIDEBAR_DEFAULT : SIDEBAR_MIN;
    setSidebarWidth(next);
    localStorage.setItem('sidebar_width', String(next));
  };

  const isActive = (path: string) => location.pathname === path;
  // Usar userProfile (datos reales del backend) con fallback al token de Cognito
  const displayName = userProfile?.name ?? user?.name ?? '';
  const displayRole = userProfile?.role ?? 'Usuario';
  const displayEmail = userProfile?.email ?? user?.email ?? '';
  const initials = displayName
    ? displayName.split(' ').map((n: string) => n[0]).slice(0, 2).join('').toUpperCase()
    : 'U';

  // Ítems del nav según rol (compartidos desktop/mobile)
  const navItems = [
    isAdmin() ? { path: '/dashboard',     label: 'Dashboard',      icon: (a: boolean) => <IconDashboard active={a} /> } : null,
    {            path: '/conversations',  label: 'Chats',          icon: (a: boolean) => <IconConversations active={a} /> },
    {            path: '/board',          label: 'Tablero',        icon: (a: boolean) => <IconBoard active={a} /> },
    !isAdvisor() ? { path: '/companies', label: 'Agentes',        icon: (a: boolean) => <IconAgents active={a} /> } : null,
    isAdmin() ? { path: '/users',         label: 'Usuarios',       icon: (a: boolean) => <IconUsers active={a} /> } : null,
  ].filter(Boolean) as { path: string; label: string; icon: (a: boolean) => React.ReactNode }[];

  return (
    <div className="flex h-screen bg-brand-bg overflow-hidden">

      {/* ══════════════════════════════════════════════════════════════
          SIDEBAR — solo visible en md+
      ══════════════════════════════════════════════════════════════ */}
      <aside
        ref={sidebarRef}
        className="hidden md:flex flex-col bg-brand-surface border-r border-brand-border shadow-sm flex-shrink-0 relative transition-colors duration-200"
        style={{ width: sidebarWidth }}
      >
        {/* Logo */}
        <div className={`flex items-center gap-3 px-4 py-5 border-b border-brand-border ${collapsed ? 'justify-center px-0' : ''}`}>
          <div className="logo-k w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center">
            <span className="logo-k-letter font-extrabold text-sm">K</span>
          </div>
          {!collapsed && (
            <div className="leading-tight min-w-0">
              <p className="text-brand-dark font-bold text-sm truncate">Kreando Software</p>
              <p className="text-brand-mid text-xs truncate">Agentes</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 p-3 space-y-2 mt-2 overflow-hidden">
          {navItems.map(item => (
            <NavItem
              key={item.path}
              label={item.label}
              icon={item.icon(isActive(item.path))}
              active={isActive(item.path)}
              collapsed={collapsed}
              onClick={() => handleNav(item.path)}
            />
          ))}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-brand-border">
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className={`w-full flex items-center gap-3 p-2 rounded-xl hover:bg-brand-light transition ${collapsed ? 'justify-center' : ''}`}
            >
              <div className="avatar-initial w-8 h-8 rounded-full bg-brand-dark flex items-center justify-center flex-shrink-0">
                <span className="text-brand-yellow font-bold text-xs">{initials}</span>
              </div>
              {!collapsed && (
                <div className="flex-1 text-left min-w-0">
                  <p className="text-brand-dark text-xs font-semibold truncate">{displayName}</p>
                  <p className="text-brand-mid text-[10px] truncate">{displayEmail}</p>
                </div>
              )}
            </button>

            {menuOpen && (
              <div className="absolute bottom-full left-0 mb-2 w-52 bg-brand-surface rounded-xl shadow-2xl z-50 border border-brand-border overflow-hidden">
                <div className="px-4 py-3 border-b border-brand-border">
                  <p className="text-xs font-semibold text-brand-dark truncate">{displayName}</p>
                  <p className="text-[10px] text-brand-mid truncate capitalize">{displayRole}</p>
                </div>
                <button onClick={handleToggleSound} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-brand-dark hover:bg-brand-light transition">
                  <span className="font-medium whitespace-nowrap">{soundEnabled ? 'Sonido activado' : 'Sonido desactivado'}</span>
                  {soundEnabled ? (
                    <svg className="w-4 h-4 ml-2 flex-shrink-0 text-brand-mid" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M12 6v12m0 0l-3-3m3 3l3-3M9 9H5a1 1 0 00-1 1v4a1 1 0 001 1h4l5 5V4L9 9z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 ml-2 flex-shrink-0 text-brand-mid" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                  )}
                </button>
                <button onClick={handleLogout} className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-brand-light transition font-medium border-t border-brand-border">
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>

          <button onClick={toggleTheme} title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            className={`mt-1 w-full flex items-center gap-3 p-2 rounded-xl hover:bg-brand-light transition text-brand-mid hover:text-brand-dark overflow-hidden ${collapsed ? 'justify-center' : ''}`}>
            {isDark ? (
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
              </svg>
            ) : (
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            )}
            {!collapsed && <span className="text-xs font-medium truncate whitespace-nowrap">{isDark ? 'Modo claro' : 'Modo oscuro'}</span>}
          </button>
        </div>

        {/* Handle de redimensionado */}
        <div onMouseDown={onDragHandleMouseDown} onDoubleClick={onDragHandleDblClick}
          title="Arrastra para redimensionar · Doble click para colapsar/expandir"
          className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize group">
          <div className="absolute inset-y-0 right-0 w-px bg-transparent group-hover:bg-brand-yellow/60 transition-colors duration-150" />
          <div className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-1/2 w-3 h-8 rounded-full bg-brand-border group-hover:bg-brand-yellow/80 transition-colors duration-150 opacity-0 group-hover:opacity-100" />
        </div>
      </aside>

      {/* ══════════════════════════════════════════════════════════════
          CONTENIDO PRINCIPAL
          — En mobile: padding-bottom para no quedar bajo la bottom nav
      ══════════════════════════════════════════════════════════════ */}
      <main className="flex-1 overflow-hidden transition-colors duration-200">
        <div className="h-full overflow-y-auto main-content-mobile">
          {children}
        </div>
      </main>

      {/* ══════════════════════════════════════════════════════════════
          BOTTOM NAV — solo visible en mobile (< md)
      ══════════════════════════════════════════════════════════════ */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-brand-surface border-t border-brand-border safe-area-inset-bottom">
        <div className="flex items-stretch h-14">
          {navItems.map(item => (
            <BottomNavItem
              key={item.path}
              label={item.label}
              icon={item.icon(isActive(item.path))}
              active={isActive(item.path)}
              onClick={() => handleNav(item.path)}
            />
          ))}
          {/* Botón de usuario — solo iniciales, sin label */}
          <button
            ref={avatarBtnRef}
            onClick={() => setMenuOpen(!menuOpen)}
            className={`flex-1 flex flex-col items-center justify-center py-2 transition-colors relative
              ${menuOpen ? 'text-brand-yellow' : 'text-brand-mid'}`}
          >
            <div className="avatar-initial w-7 h-7 rounded-full bg-brand-dark flex items-center justify-center flex-shrink-0">
              <span className="text-brand-yellow font-bold text-[11px]">{initials}</span>
            </div>
            {menuOpen && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-brand-yellow rounded-full" />}
          </button>
        </div>
      </nav>

      {/* ══════════════════════════════════════════════════════════════
          MENÚ DE USUARIO MOBILE — sheet desde abajo
      ══════════════════════════════════════════════════════════════ */}
      {menuOpen && (
        <>
          {/* Sheet de opciones — sin overlay, se cierra con el useEffect de mousedown */}
          <div ref={sheetRef} className="md:hidden fixed bottom-14 left-0 right-0 z-50 bg-brand-surface border-t border-brand-border rounded-t-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-brand-border">
              <p className="text-sm font-bold text-brand-dark">{displayName}</p>
              <p className="text-xs text-brand-mid capitalize">{displayRole}</p>
            </div>
            <button
              onClick={() => { handleToggleSound(); setMenuOpen(false); }}
              className="w-full flex items-center justify-between px-5 py-3.5 text-sm text-brand-dark active:bg-brand-light transition">
              <span className="font-medium">{soundEnabled ? 'Sonido activado' : 'Sonido desactivado'}</span>
              {soundEnabled ? (
                <svg className="w-4 h-4 text-brand-mid" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072M9 9H5a1 1 0 00-1 1v4a1 1 0 001 1h4l5 5V4L9 9z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-brand-mid" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              )}
            </button>
            <button
              onClick={() => { toggleTheme(); setMenuOpen(false); }}
              className="w-full flex items-center justify-between px-5 py-3.5 text-sm text-brand-dark active:bg-brand-light transition border-t border-brand-border">
              <span className="font-medium">{isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}</span>
              {isDark ? (
                <svg className="w-4 h-4 text-brand-mid" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-brand-mid" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>
            <button
              onClick={handleLogout}
              className="w-full text-left px-5 py-3.5 text-sm text-red-500 active:bg-brand-light transition font-medium border-t border-brand-border">
              Cerrar sesión
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default Layout;
